import { isEmpty } from 'lodash';

import { getNodes } from '@/utils/graphqlUtils';

import {
  PresentationViewSource_ClientOrganizationFragment,
  PresentationViewSource_ClientProfileFragment,
  PresentationViewSource_EntityFragment,
  PresentationViewSource_TestamentaryEntityFragment,
} from './graphql/PresentationViewSource.generated';

/**
 * What a record's "View source" affordance can open. A record with nothing to
 * cite gets NO citation at all (see {@link buildPresentationViewSourceMap}), so
 * the presence of one of these is the "can view source" predicate — callers
 * never have to check both fields.
 */
export interface PresentationViewSourceCitation {
  /**
   * The onboarding suggestion whose cited evidence to highlight. Preferred over
   * the source-document fallback when both exist.
   */
  suggestionId?: string;
  /**
   * Whether the entity has explicit document associations for the fallback
   * drawer. Any relationship type counts, not only SOURCE_DOCUMENT.
   *
   * Only ever true for entities. `HouseholdDocumentsViewer` has no query branch
   * for a client organization (it throws), and its client-profile branch lists
   * household-wide wills/POAs filtered by document type rather than anything
   * linked to that person — so neither is a meaningful "source" for them.
   */
  hasSourceDocument: boolean;
}

/**
 * The citation for a single entity, or null when there's nothing to cite: the
 * onboarding suggestion's evidence when present, otherwise its explicitly
 * associated documents, regardless of relationship type.
 *
 * Exported on its own (not just via the map) so the entity cover slide can
 * resolve its own citation off the entity it already holds — that slide is shared
 * with the entity preview modal, which has no designer context to read a map
 * from.
 */
export function getEntityViewSourceCitation(
  entity: PresentationViewSource_EntityFragment
): PresentationViewSourceCitation | null {
  // An entity is created by at most one onboarding suggestion, so the first
  // element is the only one. (The schema models it as a list because it's an
  // inverse edge.)
  const suggestionId = entity.onboardingEntitySuggestion?.[0]?.id;
  // `document` is non-null on EntityDocumentRelationship, so any explicit
  // association implies a document to show in the unfiltered fallback drawer.
  const hasSourceDocument = !isEmpty(
    getNodes(entity.viewSourceDocumentRelationships)
  );

  if (!suggestionId && !hasSourceDocument) {
    return null;
  }

  return { suggestionId, hasSourceDocument };
}

/** The record lists the presentation query loads citations from. */
interface PresentationViewSourceRecords {
  entities: PresentationViewSource_EntityFragment[];
  clientProfiles: PresentationViewSource_ClientProfileFragment[];
  clientOrganizations: PresentationViewSource_ClientOrganizationFragment[];
  testamentaryEntities: PresentationViewSource_TestamentaryEntityFragment[];
}

/**
 * Citations for every household record that has one, keyed by record id.
 *
 * Pulids are globally unique and kind-prefixed, so one map serves entities,
 * individuals, organizations, and sub-trusts alike — a hover toolbar keys into it
 * with whichever id its tile targets, without knowing the kind. Records with
 * nothing to cite are omitted, so a missing key means "no View source button".
 *
 * Individuals, organizations, and sub-trusts are suggestion-only: the query
 * already filters those lists to records that have an onboarding suggestion, and
 * there's no source-document fallback for them (see
 * {@link PresentationViewSourceCitation}).
 */
export function buildPresentationViewSourceMap({
  entities,
  clientProfiles,
  clientOrganizations,
  testamentaryEntities,
}: PresentationViewSourceRecords): Record<
  string,
  PresentationViewSourceCitation
> {
  const map: Record<string, PresentationViewSourceCitation> = {};

  entities.forEach((entity) => {
    const citation = getEntityViewSourceCitation(entity);
    if (citation) {
      map[entity.id] = citation;
    }
  });

  clientProfiles.forEach(({ id, onboardedIndividualSuggestion }) => {
    const suggestionId = onboardedIndividualSuggestion?.[0]?.id;
    if (suggestionId) {
      map[id] = { suggestionId, hasSourceDocument: false };
    }
  });

  clientOrganizations.forEach(({ id, onboardedOrganizationSuggestion }) => {
    const suggestionId = onboardedOrganizationSuggestion?.[0]?.id;
    if (suggestionId) {
      map[id] = { suggestionId, hasSourceDocument: false };
    }
  });

  testamentaryEntities.forEach(
    ({ id, onboardingTestamentaryEntitySuggestion }) => {
      const suggestionId = onboardingTestamentaryEntitySuggestion?.[0]?.id;
      if (suggestionId) {
        map[id] = { suggestionId, hasSourceDocument: false };
      }
    }
  );

  return map;
}
